import classNames from 'classnames';
import { KeyboardEvent, MouseEvent, useRef } from 'react';
import { KeyCode } from '../../types';
import ClassName from '../../types/ClassName';
import TextField from '../TextField/TextField';

export interface ITimeTotalFieldProps {
  className?: ClassName;
  tabIndex?: number;
  forwardRef?: (ref: HTMLInputElement) => void;
  inputs?: { inputValue?: string };
  value: string;
  disabled: boolean;
  error?: boolean;
  onFocus?: () => void;
  onBlur?: (val: string) => void;
  placeholder: string;
  minuteStep: number;
  adjustMinutes?: (val: number) => void;
  adjustHours?: (val: number) => void;
  handleChange?: (val: string | null) => void;
}

const TimeTotalField = (props: ITimeTotalFieldProps) => {
  const {
    className,
    disabled,
    error,
    onFocus,
    onBlur,
    placeholder,
    minuteStep,
    adjustMinutes,
    adjustHours,
    tabIndex,
    value,
    forwardRef,
    handleChange,
  } = props;

  const classes = classNames('time-total-field', className);

  const input = useRef<HTMLInputElement>();
  const select = useRef<HTMLSelectElement | null>();
  const selected = useRef<'minutes' | 'hours'>('hours');

  function assignInput(element: HTMLInputElement) {
    input.current = element;
    forwardRef?.(element);
  }

  function onTextFieldClick(_val: string, _event: MouseEvent) {
    if (input.current) {
      input.current.select();
    }
  }

  function handleInputBlur(val: string) {
    if (onBlur) {
      onBlur(val);
    }
  }

  function handleInputFocus() {
    selected.current = 'hours';
    if (input.current) {
      input.current.select();
    }

    if (onFocus) {
      onFocus();
    }
  }

  // highlights the entire value of the time field
  function selectHours() {
    selected.current = 'hours';

    if (input.current) {
      const startPos = 0;
      const endPos = input.current.value.indexOf(':');

      if (endPos) {
        input.current.selectionStart = startPos;
        input.current.selectionEnd = endPos;
      }
    }
  }

  function selectMinutes() {
    selected.current = 'minutes';

    if (input.current) {
      let startPos = input.current.value.indexOf(':');
      let endPos = 5;

      if (startPos) {
        startPos += 1;
        endPos = 5;
        input.current.selectionStart = startPos;
        input.current.selectionEnd = endPos;
      }
    }
  }

  function internalAdjustHours(val: number) {
    if (adjustHours) {
      adjustHours(val);
    }
    setTimeout(() => selectHours(), 0);
  }

  function internalAdjustMinutes(val: number) {
    if (adjustMinutes) {
      adjustMinutes(val);
    }
    setTimeout(() => selectMinutes(), 0);
  }

  function handleKeyDown(_val: string, event: KeyboardEvent) {
    const keyCode = event.keyCode;
    const arrowKeyCodes = [KeyCode.ARROW_LEFT, KeyCode.ARROW_UP, KeyCode.ARROW_RIGHT, KeyCode.ARROW_DOWN];

    if (arrowKeyCodes.includes(keyCode)) {
      event.preventDefault();
      switch (keyCode) {
        case KeyCode.ARROW_LEFT:
          selectHours();
          break;
        case KeyCode.ARROW_RIGHT:
          if (selected.current === 'minutes') {
            if (select.current) {
              select.current.focus();
            }
          } else {
            selectMinutes();
          }
          break;
        case KeyCode.ARROW_DOWN:
          if (selected.current === 'minutes') {
            internalAdjustMinutes(-minuteStep);
          } else {
            internalAdjustHours(-1);
          }
          break;
        default:
          // 38
          if (selected.current === 'minutes') {
            internalAdjustMinutes(minuteStep);
          } else {
            internalAdjustHours(1);
          }
          break;
      }
    }
  }

  function handleInputChange(val: string) {
    if (handleChange) {
      handleChange(val);
    }
  }

  return (
    <div className={classes}>
      <TextField
        tabIndex={tabIndex}
        onClick={onTextFieldClick}
        value={value}
        disabled={disabled}
        inputRef={assignInput}
        error={error}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        placeholder={placeholder}
      />
    </div>
  );
};

export default TimeTotalField;
